import * as React from 'react';
import { cn } from '@/lib/utils';
import { getAvatarColor, getAvatarTextColor, getInitials } from '@/lib/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

interface AvatarUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

interface UserAvatarProps {
  user: AvatarUser;
  size?: AvatarSize;
  showTooltip?: boolean;
  className?: string;
}

// Tamaños -> px (24/32/40/56 según spec) + escala tipográfica acorde.
const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-14 w-14 text-base',
};

function buildFullName(user: AvatarUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email;
}

const UserAvatar = React.forwardRef<HTMLSpanElement, UserAvatarProps>(
  ({ user, size = 'md', showTooltip = false, className }, ref) => {
    const initials = getInitials(user);
    const bg = getAvatarColor(user.id);
    const fg = getAvatarTextColor(bg);
    const fullName = buildFullName(user);

    const avatar = (
      <span
        ref={ref}
        role="img"
        aria-label={fullName}
        className={cn(
          'inline-flex select-none items-center justify-center rounded-full font-semibold leading-none',
          SIZE_CLASSES[size],
          className,
        )}
        style={{ backgroundColor: bg, color: fg }}
      >
        {initials}
      </span>
    );

    if (!showTooltip) return avatar;

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>{avatar}</TooltipTrigger>
          <TooltipContent>
            <div className="flex flex-col">
              <span className="font-medium">{fullName}</span>
              <span className="text-text-muted">{user.email}</span>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  },
);
UserAvatar.displayName = 'UserAvatar';

export { UserAvatar };
export type { UserAvatarProps };
